/**
 * Our own storefront tracking (the data comes from snippets/cf-tracking.liquid).
 * ---------------------------------------------------------------------------
 *
 * Sends each storefront event to the event server (repo cityframes-tracking), which builds the
 * Meta Conversions API event from it. With `metaLive` on (since the cutover, 1 Oct 2026) it also
 * fires the Meta pixel with the same event ID, so Meta keeps one of the two. With it off the
 * server only builds and stores what it would send. Elevar, which fed Meta before, is gone.
 * With `google.live` on it also runs our own Google tag (GA4, Google Ads, Merchant Center) in place
 * of the Google & YouTube app; see "Google" below.
 *
 * Events: PageView (every page), ViewContent (a city the visitor chose), ViewCategory
 * (collections), Search (search results), AddToCart / AddOnAdded (every /cart/add call and every
 * quantity increase in the cart), CustomizeProduct (window.cfTrack from the configurators), Lead
 * (Klaviyo form sign-ups), QualifiedVisit (a serious shopper, see below), CreateCustomCube /
 * CreateCustomFrame (a custom-location design made), PageEngagement (sections reached and clicks
 * on the pages in config.engagementPages; stored only). Checkout and purchase come from the
 * checkout pixel, not from here.
 *
 * What an add to cart reports follows the tracking spec of 28 Sep 2026 (PLAN.md in
 * cityframes-tracking): one event per click, however many cart lines it makes; a CUBE Set, an
 * Individual Trio or an Individual Collection as its cities at their share of the set price;
 * add-ons inside the event of the product they came with, or as AddOnAdded when added on their
 * own; every value net.
 *
 * Consent: nothing is stored or sent without marketing consent. Events that happen before the
 * visitor answers the banner wait in memory and go out if they accept — the landing page view
 * carries the ad click, so losing it would lose the attribution.
 *
 * Our own testing: the Claude app's browser pane counts as a visitor who declined, whatever its
 * banner says. A session testing set carts on the live shop from it sent Meta 39 add-to-carts
 * worth up to EUR 1.8 million (2 Oct 2026, 01:33–01:40); the lab uses headless Chrome, which
 * blocks Meta and the event server itself, so nothing that tests on purpose goes through here.
 */
(() => {
  const node = document.querySelector('script[type="application/json"][data-cf-tracking]');
  if (!node) return;

  let config;
  try {
    config = JSON.parse(node.textContent);
  } catch (error) {
    return;
  }

  const STANDARD = new Set(['PageView', 'ViewContent', 'Search', 'AddToCart', 'CustomizeProduct', 'Lead']);
  const FROM_THEME = new Set(['CustomizeProduct']);  // what window.cfTrack accepts
  const CART_ADD = /\/cart\/add(\.js|\.json)?\/?$/;   // same match as cf-sales-block.js, locale prefix included
  const CART_CHANGE = /\/cart\/(change|update)(\.js|\.json)$/;
  const landedAt = Date.now();
  const landingParams = new URLSearchParams(window.location.search);

  let consent = null;     // null until known; then { marketing, analytics }
  const waiting = [];     // events from before the banner was answered
  const outbox = [];
  let flushTimer = null;
  const viewed = new Set();  // variant IDs already reported as ViewContent on this page
  const cityQueue = [];      // Klaviyo city views from before the banner was answered
  const browseQueue = [];    // the same for cities added to the cart (browseFromCart)

  // --- small helpers -------------------------------------------------------------------------

  const uuid = () => (window.crypto && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

  const readCookie = (name) => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
  };

  const writeCookie = (name, value, days, domain) => {
    document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${days * 86400}; path=/;${domain ? ` domain=${domain};` : ''} SameSite=Lax; Secure`;
  };

  // Meta's pixel keeps its cookies on the shop's root domain (".cityframes.de"), so ours go there too.
  const rootDomain = `.${window.location.hostname.replace(/^www\./, '')}`;

  const store = {
    get(key) { try { return window.localStorage.getItem(key); } catch (error) { return null; } },
    set(key, value) { try { window.localStorage.setItem(key, value); } catch (error) { /* private mode */ } },
    remove(key) { try { window.localStorage.removeItem(key); } catch (error) { /* private mode */ } }
  };

  const sha256 = async (text) => {
    const bytes = new TextEncoder().encode(text);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
  };

  // --- identity (only ever touched with marketing consent) -----------------------------------

  let visitorId = null;

  /**
   * One ID per browser, sent to Meta hashed as external_id. A visitor Elevar already knows keeps
   * Elevar's ID, so Meta sees the same person across the switch.
   */
  function getVisitorId() {
    if (visitorId) return visitorId;
    let id = readCookie('_cf_vid');
    if (!validId(id)) {
      const elevar = (store.get('___ELEVAR_GTM_SUITE--userId') || '').replace(/"/g, '');
      id = validId(elevar) ? elevar : (shopifyId() || uuid());
    }
    writeCookie('_cf_vid', id, 365);
    visitorId = id;
    return id;
  }

  /**
   * Shopify's own visitor ID — the checkout pixel's event.clientId. Safari deletes a cookie that a
   * script wrote after 7 days without a visit, ours included, and the next visit looked like a new
   * person. Shopify writes this ID into every page from a cookie its server sets, which Safari
   * keeps (tested 4 Oct 2026: with every script cookie and storage wiped, Shopify's ID and the
   * consent came back unchanged, ours did not). So a new visitor's ID is Shopify's, and every
   * event carries Shopify's too, for the server to join our older IDs to it.
   */
  function shopifyId() {
    let id = '';
    try { id = window.ShopifyAnalytics.lib.user().traits().uniqToken || ''; } catch (error) { /* not on this page */ }
    if (!validId(id)) id = readCookie('shopify_client_id');
    return validId(id) ? id : '';
  }

  /**
   * Shopify's analytics answers a zero UUID before it knows the visitor: 00000000-0000-0000-5000-
   * 000000000000, the version digit its only mark. Taken as an ID from 4 to 13 Oct 2026, it made
   * some 2,500 events of different people one visitor (and one external_id at Meta). Zeros with at
   * most one other digit are never an ID — not new, not stored: a browser that kept it gets a real
   * one on its next page.
   */
  function validId(id) {
    return /^[A-Za-z0-9_-]{8,64}$/.test(id || '') && !/^[0-]*[0-9a-f]?[0-]*$/i.test(id);
  }

  /**
   * Meta's browser ID (_fbp) and ad-click ID (_fbc). Meta's pixel sets them when it loads, which
   * on the first page after consent is after our first send — half of those page views went out
   * without _fbp. So when one is missing it is set here, in Meta's documented format and on the
   * root domain; Meta's pixel adopts cookies it finds, so browser and server events carry the same
   * values from the first page view. A new fbclid replaces an older click.
   */
  function metaIds() {
    let fbp = readCookie('_fbp');
    if (!fbp) {
      fbp = `fb.1.${Date.now()}.${Math.floor(Math.random() * 2147483647)}`;
      writeCookie('_fbp', fbp, 90, rootDomain);
    }
    let fbc = readCookie('_fbc');
    const fbclid = landingParams.get('fbclid');
    if (fbclid && /^[A-Za-z0-9_-]{10,500}$/.test(fbclid) && !(fbc && fbc.endsWith(`.${fbclid}`))) {
      fbc = `fb.1.${landedAt}.${fbclid}`;
      writeCookie('_fbc', fbc, 90, rootDomain);
    }
    return { fbp, fbc: fbc || readCookie('_cf_fbc') };
  }

  function emailHash() {
    if (config.customerEmailHash) store.set('cf_em', config.customerEmailHash);
    return store.get('cf_em');
  }

  /**
   * The last Google Ads click (gclid, or gbraid / wbraid from iOS), kept 90 days on the root domain
   * as "<kind>.<landing time ms>.<id>" (_cf_gcl): the event server uploads a purchase against it
   * (Google Ads API), and the checkout pixel and the cart read the same cookie. 90 days is Google's
   * click window. A new click replaces an older one.
   */
  const GOOGLE_CLICKS = ['gclid', 'gbraid', 'wbraid'];
  const GOOGLE_CLICK = /^(gclid|gbraid|wbraid)\.(\d{10,13})\.([A-Za-z0-9_-]{10,500})$/;

  function googleClickCookie() {
    let saved = readCookie('_cf_gcl');
    const kind = GOOGLE_CLICKS.find((key) => /^[A-Za-z0-9_-]{10,500}$/.test(landingParams.get(key) || ''));
    if (kind && !(saved && saved.endsWith(`.${landingParams.get(kind)}`))) {
      saved = `${kind}.${landedAt}.${landingParams.get(kind)}`;
      writeCookie('_cf_gcl', saved, 90, rootDomain);
    }
    return saved && GOOGLE_CLICK.test(saved) ? saved : '';
  }

  /**
   * Google Analytics' IDs for this browser, from the cookies its tag keeps: the client ID (_ga,
   * "GA1.1.<id>") and the current session (_ga_<stream>, "GS1.1.<session>.…" or, since 2025,
   * "GS2.1.s<session>$…"). The server sends checkout steps and the purchase to GA4 under them,
   * so they land in the session that led there.
   */
  function googleIds() {
    const out = {};
    const click = googleClickCookie().match(GOOGLE_CLICK);
    if (click) out[click[1]] = click[3];
    const ga = (readCookie('_ga') || '').match(/^GA\d\.\d\.(\d+\.\d+)$/);
    if (ga) out.ga_cid = ga[1];
    const stream = google.ga4 ? readCookie(`_ga_${google.ga4.replace(/^G-/, '')}`) || '' : '';
    const session = stream.match(/^GS1\.\d\.(\d+)\./) || stream.match(/^GS2\.\d\.s(\d+)/);
    if (session) out.ga_sid = session[1];
    return out;
  }

  // --- Meta pixel (only with metaLive) -------------------------------------------------------

  let metaReady = null;

  function loadMeta() {
    if (metaReady) return metaReady;
    /* eslint-disable */
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    metaIds();  // before fbevents.js runs, so it adopts our _fbp / _fbc instead of making its own
    // Lowercased before hashing, as Meta's pixel does, so this matches the server's external_id.
    metaReady = sha256(getVisitorId().toLowerCase()).then((externalId) => {
      const matching = { external_id: externalId };
      const em = emailHash();
      if (em) matching.em = em;
      // The shop's market country: the one customer detail every visitor has. Meta's pixel hashes it.
      if (config.country) matching.country = String(config.country).toLowerCase();
      // No automatic events: Meta would otherwise report every button click as
      // "SubscribedButtonClick" (seen in Test Events 2026-09-27). Elevar had this off too.
      window.fbq('set', 'autoConfig', false, config.pixelId);
      window.fbq('init', config.pixelId, matching);
    });
    return metaReady;
  }

  function metaCustomData(data) {
    const out = {};
    ['currency', 'value', 'content_ids', 'contents', 'content_type', 'content_name', 'content_category', 'num_items',
      'search_string', 'set_type', 'set_size', 'has_addons', 'grand_frame', 'grand_background', 'qualify_reason']
      .forEach((key) => { if (data[key] !== undefined) out[key] = data[key]; });
    if (out.contents && !out.content_type) out.content_type = 'product';
    return out;
  }

  // --- Google: GA4, Google Ads and Merchant Center (instead of the Google & YouTube app) -------
  //
  // One Google tag (config.google.tag, whose destinations are GA4 and Merchant Center) plus the
  // Google Ads tag, loaded once the visitor has given marketing consent AND the page has finished
  // loading, on its own data layer (cfDataLayer), so nothing else on the page shares its queue.
  // Events from before it loads wait in that queue. Values are the net values Meta gets.
  //
  // GA4 gets the shop's events under Google's names (view_item, view_item_list, search,
  // add_to_cart, generate_lead) and ours under our own (customize_product, qualified_visit,
  // create_custom_cube, create_custom_frame); items carry the variant ID, the feed's g:id, for
  // dynamic remarketing. Checkout steps and the purchase come from the event server (Measurement
  // Protocol and the Google Ads API), since no tag can run in Shopify's checkout.
  //
  // `live` (snippets/cf-tracking.liquid) is on since 15 Oct 2026, when the app's pixel went. The two
  // cannot run side by side: Google runs one copy of a tag per page, and with the app's copy of the
  // same tag IDs loaded first ours was ignored (lab, 14 Oct 2026). `?cf_google=debug` marks this
  // browser's events for GA4's DebugView; `?cf_google=off` stops that.

  const google = config.google || {};
  const GOOGLE_DEBUG_KEY = 'cf_google_debug';
  if (landingParams.get('cf_google') === 'debug') store.set(GOOGLE_DEBUG_KEY, '1');
  else if (landingParams.get('cf_google') === 'off') store.remove(GOOGLE_DEBUG_KEY);
  const googleDebug = store.get(GOOGLE_DEBUG_KEY) === '1';
  const googleOn = !!(google.live && google.tag);

  const GA4_EVENTS = {
    ViewContent: 'view_item', ViewCategory: 'view_item_list', Search: 'search', AddToCart: 'add_to_cart',
    AddOnAdded: 'add_to_cart', Lead: 'generate_lead', CustomizeProduct: 'customize_product',
    QualifiedVisit: 'qualified_visit', CreateCustomCube: 'create_custom_cube', CreateCustomFrame: 'create_custom_frame'
  };
  // Google Ads conversions a page can fire, by label (config.google.labels). None are set: Google
  // Ads gets purchase and checkout steps from the event server; the Ads tag here is remarketing.
  const ADS_CONVERSIONS = { PageView: 'page_view', ViewContent: 'view_item', Search: 'search', AddToCart: 'add_to_cart', AddOnAdded: 'add_to_cart' };

  let googleLoaded = false;

  function gtag() {
    window.cfDataLayer.push(arguments);
  }

  const googleConsent = () => ({
    ad_storage: consent.marketing ? 'granted' : 'denied',
    ad_user_data: consent.marketing ? 'granted' : 'denied',
    ad_personalization: consent.marketing ? 'granted' : 'denied',
    analytics_storage: consent.analytics ? 'granted' : 'denied'
  });

  function loadGoogle() {
    if (googleLoaded) return;
    googleLoaded = true;
    window.cfDataLayer = window.cfDataLayer || [];
    gtag('consent', 'default', googleConsent());
    gtag('js', new Date());
    const em = emailHash();
    if (em) gtag('set', 'user_data', { sha256_email_address: em });
    // The content group goes in the tag's configuration: a `set` of it never reached the page view (lab, 15 Oct 2026).
    gtag('config', google.tag, { content_group: contentGroup(), ...(googleDebug ? { debug_mode: true } : {}) });
    if (google.ads) gtag('config', google.ads, { allow_enhanced_conversions: true });
    const inject = () => {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(google.tag)}&l=cfDataLayer`;
      // Once the tag has written its cookies, the cart gets GA's IDs for an express checkout.
      script.addEventListener('load', () => setTimeout(syncCartIds, 2000));
      document.head.appendChild(script);
    };
    const idle = () => (window.requestIdleCallback ? window.requestIdleCallback(inject, { timeout: 3000 }) : setTimeout(inject, 1));
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  /**
   * GA4's "Content group": the page's path without its language folder (/de/, /fr/ …), so a page
   * is one row in GA4 whichever language it was seen in (Reports → Pages and screens → Content
   * group). Page path itself keeps the folder; the shop's domains (.de, .uk …) are one path already.
   */
  function contentGroup() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    let path = window.location.pathname;
    if (root !== '/' && `${path}/`.startsWith(root)) path = `/${path.slice(root.length)}`;
    return path.replace(/\/+$/, '') || '/';
  }

  function googleParams(name, data) {
    const params = {};
    if (data.currency) params.currency = data.currency;
    if (data.value !== undefined) params.value = data.value;
    let items = (data.contents || []).map((c) => ({ item_id: String(c.id), price: c.item_price, quantity: c.quantity || 1 }));
    if (!items.length && name === 'ViewCategory') {
      items = (data.content_ids || []).map((id, index) => ({ item_id: String(id), index }));
      params.item_list_id = data.content_category;
      params.item_list_name = data.content_name;
    } else if (items.length === 1 && data.content_name) {
      items[0].item_name = data.content_name;
    }
    items.forEach((item) => {
      if (data.content_category && name !== 'ViewCategory') item.item_category = data.content_category;
      item.google_business_vertical = 'retail';
    });
    if (items.length) params.items = items;
    if (data.search_string) params.search_term = data.search_string;
    if (name === 'Lead') params.lead_source = data.content_name;
    if (data.qualify_reason) params.reason = data.qualify_reason;
    if (name === 'CustomizeProduct') params.customize_kind = data.content_category;
    ['set_type', 'set_size', 'grand_frame', 'grand_background'].forEach((key) => {
      if (data[key] !== undefined) params[key] = data[key];
    });
    params.content_group = contentGroup();
    if (googleDebug) params.debug_mode = true;
    return params;
  }

  function toGoogle(event) {
    loadGoogle();
    const data = event.data || {};
    const name = GA4_EVENTS[event.name];
    const params = googleParams(event.name, data);
    if (name) gtag('event', name, params);
    const label = google.ads && google.labels && google.labels[ADS_CONVERSIONS[event.name]];
    if (label) {
      const conversion = { send_to: `${google.ads}/${label}` };
      if (params.value !== undefined) Object.assign(conversion, { value: params.value, currency: params.currency });
      gtag('event', 'conversion', conversion);
    }
  }

  // GA4-only events from theme code (the Set offer window, 25 Oct 2026): window.cfGa4(name, params), the name as GA4
  // shows it. Straight to GA4 - not Meta, not the event server, no Ads conversion - with the page's content group, and
  // like every event only with marketing consent: until the banner is answered they wait here (applyConsent). Their
  // parameters (set_offer_group, set_offer_from, choice) are custom dimensions in GA4's admin.
  const themeGa4 = [];
  function releaseThemeGa4() {
    if (consent === null || !themeGa4.length) return;
    const due = themeGa4.splice(0);
    if (!consent.marketing || !googleOn) return;
    loadGoogle();
    due.forEach(([name, params]) => gtag('event', name, { ...params, content_group: contentGroup(), ...(googleDebug ? { debug_mode: true } : {}) }));
  }

  // --- sending -------------------------------------------------------------------------------

  const nativeFetch = window.fetch;

  function flush() {
    flushTimer = null;
    if (!outbox.length) return;
    const ids = { vid: getVisitorId() };
    const shop = shopifyId();
    if (shop) ids.shop_cid = shop;
    const { fbp, fbc } = metaIds();
    if (fbp) ids.fbp = fbp;
    if (fbc) ids.fbc = fbc;
    Object.assign(ids, googleIds());
    // A click in a Klaviyo email: its _kx names the profile the email went to. The event server asks Klaviyo
    // whose it is and keeps only the email's hash, for this browser (cityframes-tracking klaviyo_ids.py, 18 Oct 2026).
    const kx = landingParams.get('_kx');
    if (kx && /^[A-Za-z0-9_-]{10,200}\.[A-Za-z0-9]{4,10}$/.test(kx)) ids.kx = kx;
    const em = emailHash();

    while (outbox.length) {
      const batch = outbox.splice(0, 20).map((event) => ({
        ...event,
        consent,
        ids,
        // The shop's market country on every event (the server hashes it), the remembered email
        // when there is one, and whatever the event brought itself (a sign-up's email) on top.
        user: Object.assign({}, config.country ? { country: config.country } : {}, em ? { em } : {}, event.user || {})
      }));
      const body = JSON.stringify({ events: batch });
      let queued = false;
      try {
        queued = navigator.sendBeacon(config.endpoint, new Blob([body], { type: 'text/plain' }));
      } catch (error) { /* fall through */ }
      if (!queued) {
        nativeFetch.call(window, config.endpoint, {
          method: 'POST', body, keepalive: true, mode: 'no-cors', headers: { 'Content-Type': 'text/plain' }
        }).catch(() => {});
      }
    }
  }

  function send(event) {
    if (config.metaLive) {
      loadMeta().then(() => {
        window.fbq(STANDARD.has(event.name) ? 'trackSingle' : 'trackSingleCustom',
          config.pixelId, event.name, metaCustomData(event.data), { eventID: event.event_id });
      });
    }
    if (googleOn) toGoogle(event);
    outbox.push(event);
    if (!flushTimer) flushTimer = setTimeout(flush, 300);
    noteForQualifiedVisit(event);
  }

  function track(name, data, user) {
    const event = {
      event_id: uuid(),
      name,
      source: 'storefront',
      ts: Date.now(),
      url: window.location.href,
      referrer: document.referrer || undefined,
      data: data || {}
    };
    if (user) event.user = user;
    if (consent === null) {
      waiting.push(event);
    } else if (consent.marketing) {
      send(event);
    }
  }

  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flush(); });

  // --- Klaviyo "Viewed City" -----------------------------------------------------------------

  // The flow's trigger filter matches `Name` against the products' English titles, but a product
  // page in another language has its own title ("Liquidación CUBE"), which never matched: these
  // products send their English title wherever the visitor is.
  const BROWSE_NAMES = {
    cube: 'CUBE', minicube: 'miniCUBE', 'minicube-clearance-sale': 'miniCUBE',
    'cube-clearance-sale': 'CUBE Clearance Sale', frame: 'FRAME', grand: 'GRAND', blok: 'BLOK Coffee Table',
    'cube-sets': 'CUBE Set', 'cube-collections': 'CUBE Collection'
  };
  // A curated Set or Collection is called by its own name: "New York - Set" becomes "New York Set".
  const browseTitle = (title) => String(title || '').replace(/ - (Set|Collection)$/, ' $1');

  function pushViewedCity(properties) {
    window.klaviyo = window.klaviyo || [];
    window.klaviyo.push(['track', 'Viewed City', properties]);
  }

  /** The Abandoned Browse email's trigger, for a city the visitor chose (see viewContent). */
  function viewedCity(city) {
    const p = config.product;
    if (!p || !p.viewedCity) return;
    if (consent === null) {
      cityQueue.push(city);
      return;
    }
    if (!consent.marketing) return;
    const name = BROWSE_NAMES[p.handle] || p.title;
    const title = browseTitle(city.variantTitle);
    const item = {
      productId: p.id, variantId: city.variantId, productTitle: name, variantTitle: title,
      price: city.price, currency: config.currency, quantity: 1, sku: city.sku, url: city.url,
      imageURL: city.image, ImageURL: city.image, brand: 'CITYFRAMES', category: p.type
    };
    pushViewedCity({
      Name: name, ProductName: name, ProductID: p.id, Brand: 'CITYFRAMES',
      Price: city.price, CompareAtPrice: city.compareAtPrice, Value: city.price, $value: city.price,
      ImageURL: city.image, URL: city.url, categories: [p.type],
      variantIds: [city.variantId], 'Variant Id': [city.variantId], variantTitles: [title],
      'Variant Title': [title], productTitles: [name], currency_code: [config.currency],
      items: [item]
    });
  }

  // --- the visitor's IDs on the cart, for purchases the checkout pixel does not see ----------
  //
  // An express payment (Shop Pay from the cart, say) can finish without loading the store's
  // checkout, where the checkout pixel runs. The event server then sends the purchase from the
  // order data (server/tracking/fallback.py in cityframes-tracking). These hidden cart
  // attributes travel onto the order and give that purchase the browser's IDs — Meta's, and
  // Google's: the Ads click (_cf_gcl) and GA's client and session (_ga, _ga_sid) — and they are its
  // proof of consent: written only with marketing consent, emptied when consent is withdrawn.
  //
  // Only a cart that exists is written to (Shopify's `cart` cookie), so no visitor gets a cart
  // just for this. The last write is remembered per cart, so a page view costs no request.

  const CART_IDS_KEY = 'cf_cart_ids';

  function syncCartIds() {
    if (!consent) return;
    const cart = readCookie('cart');
    const last = store.get(CART_IDS_KEY) || '';
    if (!cart) return;
    let attributes;
    if (consent.marketing) {
      const { fbp, fbc } = metaIds();
      const ga = googleIds();
      attributes = { _cf_vid: getVisitorId(), _fbp: fbp || '', _fbc: fbc || '', _cf_gcl: googleClickCookie(),
        _ga: ga.ga_cid || '', _ga_sid: ga.ga_sid || '' };
    } else if (last) {
      // consent withdrawn: empty what was written
      attributes = { _cf_vid: '', _fbp: '', _fbc: '', _cf_gcl: '', _ga: '', _ga_sid: '' };
    } else {
      return;
    }
    const stamp = consent.marketing ? [cart, ...Object.values(attributes)].join('|') : '';
    if (stamp === last) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    nativeFetch.call(window, `${root}cart/update.js`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ attributes })
    }).then((response) => { if (response.ok) store.set(CART_IDS_KEY, stamp); }).catch(() => {});
  }

  // --- telling Klaviyo who the visitor is -----------------------------------------------------
  //
  // Klaviyo records a browser's events (our "Viewed City", the Abandoned Browse trigger) only
  // for a visitor it recognises. It does not know a buyer from their checkout, so the checkout
  // pixel leaves the email here (cf_kl_identify) and the next page view hands it to Klaviyo once,
  // with marketing consent; Klaviyo's own cookie keeps them recognised after that. Elevar got the
  // same reach by keeping each visitor's email on its own servers. The plain email is deleted as
  // soon as it is handed over, and also when consent is refused.

  const IDENTIFY_KEY = 'cf_kl_identify';

  function identifyToKlaviyo() {
    const email = store.get(IDENTIFY_KEY);
    if (!email) return;
    store.remove(IDENTIFY_KEY);
    if (!consent || !consent.marketing || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    window.klaviyo = window.klaviyo || [];
    window.klaviyo.push(['identify', { email }]);
  }

  // --- emails a visitor gives us on the storefront ---------------------------------------------
  //
  // "Email your design" in the custom-location designers (CUBE and FRAME, one id in both): someone
  // saving a city they designed is deep in a decision. With marketing consent the email is kept as
  // a hash (cf_em, as for a newsletter sign-up), so this visitor's later events carry it, and Klaviyo
  // is told who they are, as after a checkout. The form says "We store your design & email you the
  // link"; using the hash for Meta rests on the marketing consent from the banner.

  function rememberEmail(raw) {
    const email = String(raw || '').trim().toLowerCase();
    if (!consent || !consent.marketing || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    sha256(email).then((hash) => store.set('cf_em', hash), () => {});
    window.klaviyo = window.klaviyo || [];
    window.klaviyo.push(['identify', { email }]);
  }

  document.addEventListener('click', (event) => {
    const button = event.target && event.target.closest ? event.target.closest('#cc-save-confirm') : null;
    if (!button) return;
    const input = document.getElementById('cc-save-email');
    if (input) rememberEmail(input.value);
  }, true);

  // --- QualifiedVisit: a serious shopper ------------------------------------------------------
  //
  // A custom event for judging ads by more than ~12 purchases a week, and for an Advantage+
  // audience suggestion (Alex, 1 Oct 2026): a serious shopper BEFORE the cart. Sent once per
  // visitor per 7 days, the first time they do any of the things that, in six months of PostHog
  // data, made a visitor 7–36× more likely than average to reach checkout:
  //
  //   engraving       opened the plaque engraving designer  (32% of these reached checkout)
  //   customize       a CustomizeProduct pick               (13%)
  //   pages           12 page views                         (10% at 10 pages, PostHog)
  //   configurator    reached the Individual Collection from another page of the shop (10%,
  //                   measured when it was the Trio and bundle pages)
  //   set             chose a second city for a CUBE Set (6 Oct 2026; opening the Set page used to
  //                   count as "configurator", but it is becoming a main product page, and opening
  //                   one says little)
  //   custom_location added streets or trees to a custom location (9%)
  //
  // An add to cart is not one of them (4 Oct 2026). It qualified 15% of buyers at the cart itself,
  // a minute before checkout, and AddToCart is its own event: per ad the two read as a ladder,
  // QualifiedVisit → AddToCart → Purchase. Without it, 68% of buyers still qualify before the cart,
  // and of the visitors who leave without buying, the ones who qualified come back to buy 5× as
  // often (PostHog backtest, 4 Jul–26 Sep).
  //
  // Not "3 different cities viewed" any more, and 12 pages rather than 10 (19 Oct 2026, Alex). In
  // our own events of 4–19 Oct (16,357 visitors, 41 buyers) cities was half of all QualifiedVisits
  // and the weakest reason (1.9% of them bought), and every buyer it caught qualified another way
  // before buying. Without it and at 12 pages, 4.8% of visitors qualify instead of 7.2%, 4.3% of
  // them buy instead of 2.9%, and the same 34 of the 41 buyers qualify first. The ones it misses
  // go from one product straight to the cart: AddToCart counts them, and QualifiedVisit stays a
  // measure independent of it.
  //
  // GRAND frame/background picks (CustomizeProduct with a "GRAND …" category) are left out on
  // purpose: no signal beyond add to cart. Counts
  // run over a 7-day window kept in this browser, and only with marketing consent.

  const QV_KEY = 'cf_qv';
  const QV_WINDOW_MS = 7 * 86400000;
  const QV_PAGES = 12;
  // The configurators: the CUBE Set, and the Individual Collection on
  // /products/cube-collection-individual (its old page redirects there). Not /products/cube-sets (cube-sets on 2026-10-14, cube-trios before):
  // that is every Trio, the fixed ones included, and a click on a Trio ad landed there and counted
  // (21 of 2 Oct's QualifiedVisits, 23 of the 29 leaving without a second page). Landing on a
  // configurator from outside does not count either — an ad click is not yet a shopper; one who
  // stays and picks cities qualifies by what they pick.
  const QV_CONFIGURATOR = /\/(products\/cube-collection-individual|pages\/bundle-configurator)\/?$/;

  function cameFromShop() {
    try {
      return !!document.referrer && new URL(document.referrer).hostname === window.location.hostname;
    } catch (error) {
      return false;
    }
  }

  function qvState() {
    try {
      const saved = JSON.parse(store.get(QV_KEY) || 'null');
      if (saved && Date.now() - saved.start < QV_WINDOW_MS) return saved;
    } catch (error) { /* start over */ }
    return { start: Date.now(), pages: 0, sent: false };
  }

  function qualify(reason, state) {
    if (!consent || !consent.marketing) return;
    const s = state || qvState();
    if (s.sent) return;
    s.sent = true;
    store.set(QV_KEY, JSON.stringify(s));
    track('QualifiedVisit', { qualify_reason: reason });
  }

  /** Every event that actually goes out (so: with consent) moves the counters on. */
  function noteForQualifiedVisit(event) {
    if (event.name === 'QualifiedVisit') return;
    const s = qvState();
    if (s.sent) return;
    let reason = null;
    // GRAND frame and background picks are CustomizeProduct too, but not a qualifying signal: in
    // the PostHog data they predicted nothing that add to cart did not (13 of the first 22
    // 'customize' QualifiedVisits came from them, 1 Oct).
    if (event.name === 'CustomizeProduct' && !/^GRAND /.test((event.data && event.data.content_category) || '')) reason = 'customize';
    else if (event.name === 'PageView') {
      s.pages += 1;
      if (QV_CONFIGURATOR.test(window.location.pathname) && cameFromShop()) reason = 'configurator';
      else if (s.pages >= QV_PAGES) reason = 'pages';
    }
    store.set(QV_KEY, JSON.stringify(s));
    if (reason) qualify(reason, s);
  }

  // Two of the signals are clicks no other event covers: the engraving designer (one id in every
  // configurator) and the streets / trees toggles of the custom-location designers.
  document.addEventListener('click', (event) => {
    const target = event.target && event.target.closest
      ? event.target.closest('#plaque-design-btn, #option-button-trees, #option-button-streets')
      : null;
    if (!target) return;
    qualify(target.id === 'plaque-design-btn' ? 'engraving' : 'custom_location');
  }, true);

  // --- consent -------------------------------------------------------------------------------

  // --- our own visits (15 Oct 2026) ----------------------------------------------------------
  //
  // A browser that is ours counts as a visitor who declined: nothing goes to Meta, Google or the
  // ledger. Ours are: the Claude app's browser pane (user agent "Claude/<n>"); the theme editor
  // (Shopify.designMode) and any theme but the published one (a preview); and a browser that once
  // came from the Shopify admin (admin.shopify.com — GA4's first day listed it as a source) or the
  // theme editor, or was marked with ?cf_internal=1. Those are remembered (cf_internal), so the
  // whole browser stays ours; ?cf_internal=0 undoes it. The lab's headless Chrome ("CFLab") tests
  // theme previews on purpose and keeps sending; the event server never forwards its events.

  const INTERNAL_KEY = 'cf_internal';
  const userAgent = navigator.userAgent || '';
  const shopify = window.Shopify || {};
  const lab = /\bCFLab\//.test(userAgent);
  const cameFromAdmin = (() => {
    try { return !!document.referrer && new URL(document.referrer).hostname === 'admin.shopify.com'; } catch (error) { return false; }
  })();
  if (landingParams.get('cf_internal') === '0') store.remove(INTERNAL_KEY);
  else if (landingParams.get('cf_internal') === '1' || cameFromAdmin || shopify.designMode) store.set(INTERNAL_KEY, '1');
  const preview = !!(shopify.theme && shopify.theme.role && shopify.theme.role !== 'main');
  const INTERNAL = /\bClaude\/\d/.test(userAgent)
    || (!lab && (store.get(INTERNAL_KEY) === '1' || !!shopify.designMode || preview));

  function applyConsent(marketing, analytics) {
    consent = { marketing: !!marketing && !INTERNAL, analytics: !!analytics };
    if (googleLoaded) gtag('consent', 'update', googleConsent());
    releaseThemeGa4();
    const cities = cityQueue.splice(0);
    syncCartIds();
    identifyToKlaviyo();  // before any "Viewed City" of this page, so that one counts too
    if (!consent.marketing) {
      waiting.length = 0;
      return;
    }
    while (waiting.length) send(waiting.shift());
    cities.forEach(viewedCity);
    browseQueue.splice(0).forEach(pushViewedCity);
  }

  document.addEventListener('visitorConsentCollected', (event) => {
    const detail = event.detail || {};
    applyConsent(detail.marketingAllowed, detail.analyticsAllowed);
  });

  if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
    window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
      const privacy = window.Shopify.customerPrivacy;
      if (error || !privacy || consent !== null) return;
      if (privacy.marketingAllowed()) {
        applyConsent(true, privacy.analyticsProcessingAllowed());
      } else if ((privacy.currentVisitorConsent() || {}).marketing === 'no') {
        applyConsent(false, privacy.analyticsProcessingAllowed());
      }
      // Otherwise the visitor has not answered yet: keep waiting for visitorConsentCollected.
    });
  }

  // --- net values ----------------------------------------------------------------------------
  //
  // Meta gets every value net: without VAT, duties and shipping, after discounts — the basis
  // Elevar sent, so the numbers do not jump at the switch. A storefront price includes whatever
  // the visitor's market includes, so it is scaled by netFactor(), from the `tax` metaobject:
  //
  //   EU (Shopify charges VAT)      / (1 + VAT)
  //   DDP (GB, CH, HK, MO)          / ((1 + duty) × (1 + import tax)) — our price carries both
  //   US, CA, TW                    × (1 − the duties discount's rate), wherever the cart stands.
  //                                 Above the threshold that discount hands the duties back; below
  //                                 it they are in the price at the same rate (every market's
  //                                 discount is duty / (100 + duty), tools/shipping/ddp_config.json).
  //                                 So the value is the discount's own number on both sides, read
  //                                 from its config (shop metafield cityframes.duties).
  //   DAP (AU, JP, SG, ...)         nothing to take off: the buyer pays at the door. Freight
  //                                 (GRAND, BLOK, CITYWALL) goes duty-paid there, so it comes off.

  const tax = config.tax || {};
  const pct = (n) => (Number(n) || 0) / 100;
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  function netFactor(freight) {
    if (tax.vat !== null && tax.vat !== undefined) return 1 / (1 + pct(tax.vat));
    const landed = (1 + pct(tax.duty)) * (1 + pct(tax.import));
    if (tax.allDdp) return tax.duties ? 1 - pct(tax.duties.rate) : 1 / landed;
    if (tax.freightDdp && freight) return 1 / landed;
    return 1;
  }

  // --- what an add to cart is ----------------------------------------------------------------

  // Add-ons count inside the purchase they come with, never as an add to cart of their own. The
  // same list sets custom_label_0 = addon in the Meta feed (templates/collection.facebook-feed-template).
  const ADDON_HANDLES = new Set(['engraved-plaque', 'display-cover-cube', 'display-cover-minicube',
    'plaque-alignment-tool', 'gift-note']);

  // The line properties the cart transform merges on (cityframes-cart-transform, bind.py), and the
  // variants the merged lines become: the Individual Collection's, the Individual Trio's, and the
  // CUBE Set's four sizes (3–6 cities). None is in the Meta catalog: their cities are sent.
  const SET_KEYS = { _bundle: 'individual_collection', _set: 'cube_set', _trio: 'individual_trio' };
  const SET_PARENTS = new Set(['53671941013770', '53674314596618',
    '53842518638858', '53842674286858', '53842674319626', '53842674352394']);

  // When one click holds several kinds, the event is named after the first of these it contains.
  const LEAD_ORDER = ['individual_collection', 'preset_collection', 'grand', 'cube_set', 'individual_trio', 'preset_trio',
    'blok', 'citywall', 'frame', 'single', 'other'];

  const byId = (a, b) => a.length - b.length || (a < b ? -1 : a > b ? 1 : 0);

  const handleOf = (line) => line.handle || (String(line.url || '').match(/\/products\/([^/?#]+)/) || [])[1] || '';

  function lineType(line) {
    const handle = handleOf(line);
    const type = line.product_type || '';
    if (ADDON_HANDLES.has(handle)) return 'addon';
    if (handle === 'cube-sets') return 'preset_trio';
    if (handle === 'cube-collections') return 'preset_collection';
    if (handle === 'grand') return 'grand';
    if (handle === 'blok' || type === 'BLOK') return 'blok';
    if (type === 'CITYWALL') return 'citywall';
    if (/(^|-)frame(-|$)/.test(handle)) return 'frame';
    if (/(^|-)(mini)?cube(-|$)/.test(handle)) return 'single';
    return 'other';
  }

  // Freight is what carries the `freight_ddp` tag, which cart JSON does not include: GRAND, BLOK
  // and the CITYWALLs.
  const isFreight = (line) => ['grand', 'blok', 'citywall'].includes(lineType(line));

  const lineGross = (line) => {
    const quantity = Number(line.quantity) || 1;
    const cents = line.final_line_price ?? line.line_price ?? (Number(line.final_price ?? line.price) || 0) * quantity;
    return (Number(cents) || 0) / 100;
  };

  /** The items a configurator posted — for the sets, whose cities the cart merges into one line. */
  function sentItems(init) {
    const body = init && init.body;
    if (typeof body !== 'string') return [];  // a product form's FormData: never a set
    try {
      const json = JSON.parse(body);
      return Array.isArray(json.items) ? json.items : (json.id ? [json] : []);
    } catch (error) {
      return [];
    }
  }

  function grandOptions(properties) {
    const out = {};
    try {
      const build = JSON.parse((properties && properties._grand_build) || '{}');
      Object.keys(build).forEach((key) => {
        if (/frame/i.test(key)) out.grand_frame = String(build[key]).toLowerCase();
        else if (/background/i.test(key)) out.grand_background = String(build[key]).toLowerCase();
      });
    } catch (error) { /* no build spec, no options */ }
    return out;
  }

  /**
   * The cart lines one click made, as the things the customer bought: a set with its cities,
   * or a product with its quantity. Merged set lines carry their set's property (`_bundle` /
   * `_set` / `_trio`), and the cities come from what the configurator posted under the same token.
   */
  function groupsFrom(lines, sent) {
    const groups = [];
    const sets = new Map();
    lines.forEach((line) => {
      const properties = line.properties || {};
      const key = Object.keys(SET_KEYS).find((k) => properties[k]);
      const id = String(line.variant_id || line.id);
      const quantity = Number(line.quantity) || 1;
      if (key) {
        const token = `${key}:${properties[key]}`;
        let group = sets.get(token);
        if (!group) {
          group = { type: SET_KEYS[key], key, token: properties[key], gross: 0, lines: [], title: line.product_title, line };
          sets.set(token, group);
          groups.push(group);
        }
        group.gross += lineGross(line);
        if (!SET_PARENTS.has(id)) group.lines.push({ id, quantity });
        return;
      }
      groups.push({
        type: lineType(line), id, quantity, gross: lineGross(line), title: line.product_title,
        category: line.product_type, freight: isFreight(line), properties, line
      });
    });
    groups.forEach((group) => {
      if (!group.key) return;
      const own = sent.filter((item) => item && item.properties && item.properties[group.key] === group.token);
      const source = own.length ? own : group.lines;
      group.units = [];
      source.forEach((item) => {
        for (let i = 0; i < (Number(item.quantity) || 1); i += 1) group.units.push(String(item.id));
      });
      group.cities = group.units.slice();  // in the order chosen, for the browse email
      // Sorted by variant ID, so the rounding cent lands on the same city here and in the
      // checkout pixel, which gets the cities in another order.
      group.units.sort(byId);
    });
    return groups.filter((group) => !group.units || group.units.length);
  }

  /**
   * One event for the groups. A set's net price is shared over its cities — equal shares, since
   * every CUBE has the same list price — rounded to the cent with the rounding left on the last
   * city (by variant ID), so the items add up to the set exactly. The value is the sum of the items.
   */
  function report(groups) {
    if (!groups.length) return;
    const contents = [];
    groups.forEach((group) => {
      const net = round2(group.gross * netFactor(group.freight));
      if (group.units) {
        const n = group.units.length;
        const share = round2(net / n);
        group.units.forEach((id, i) => {
          contents.push({ id, quantity: 1, item_price: i === n - 1 ? round2(net - share * (n - 1)) : share });
        });
      } else {
        contents.push({ id: group.id, quantity: group.quantity, item_price: round2(net / group.quantity) });
      }
    });

    const main = groups.filter((group) => group.type !== 'addon');
    const lead = main.slice().sort((a, b) => LEAD_ORDER.indexOf(a.type) - LEAD_ORDER.indexOf(b.type))[0];
    const data = {
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || config.currency,
      value: round2(contents.reduce((sum, c) => sum + c.item_price * c.quantity, 0)),
      content_ids: Array.from(new Set(contents.map((c) => c.id))),
      contents,
      content_type: 'product',
      content_name: (lead || groups[0]).title,
      num_items: contents.reduce((n, c) => n + c.quantity, 0),
      set_type: lead ? lead.type : 'addon',
      has_addons: groups.some((group) => group.type === 'addon')
    };
    if (lead) {
      if (lead.units) data.set_size = lead.units.length;
      else if (lead.type === 'preset_trio') {
        // A curated Set holds three to six cities (2026-10-14); its page lists them.
        const cities = window.CF && window.CF.TRIO_CITIES;
        data.set_size = Array.isArray(cities) && cities.length ? cities.length : 3;
      }
      else if (lead.type === 'preset_collection') {
        const cities = window.CF && window.CF.COLLECTION_CITIES;
        if (Array.isArray(cities) && cities.length) data.set_size = cities.length;
      } else data.set_size = 1;
      if (lead.category) data.content_category = lead.category;
      const grand = main.find((group) => group.type === 'grand');
      if (grand) Object.assign(data, grandOptions(grand.properties));
    }
    track(lead ? 'AddToCart' : 'AddOnAdded', data);
  }

  // --- Klaviyo "Viewed City" from the cart ---------------------------------------------------
  //
  // A city added straight from a grid's "+" or a Set builder never opens its product page, where
  // "Viewed City" fires - so the Abandoned Browse email never heard of most carts left behind (four
  // weeks to 2026-10-28: 28 subscriber carts without a checkout, 12 of them without any city view).
  // An add counts as a view of what was added: one event per click, for its lead product, in the
  // shape viewedCity sends. A Set or Collection someone built is "Your CUBE Set" / "Your CUBE
  // Collection" with its number of cities in `built`, their variant IDs in `cityIds` (the email
  // finds their names in the product feed and has api.cityframes.de/img/cities.jpg draw them) and
  // the cart as its link; the browse email words those its own way. Add-ons alone, Custom Locations and anything else stay out.
  const BUILT_NAMES = { cube_set: 'CUBE Set', individual_trio: 'CUBE Set', individual_collection: 'CUBE Collection' };

  function browseFromCart(groups) {
    if (consent && !consent.marketing) return;
    const main = groups.filter((group) => group.type !== 'addon' && group.line);
    const lead = main.sort((a, b) => LEAD_ORDER.indexOf(a.type) - LEAD_ORDER.indexOf(b.type))[0];
    if (!lead) return;
    const line = lead.line;
    const built = lead.units ? lead.units.length : 0;
    const name = built ? BUILT_NAMES[lead.type] : BROWSE_NAMES[handleOf(line)];
    if (!name) return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const quantity = built ? 1 : (Number(lead.quantity) || 1);
    const price = round2(lead.gross / quantity);
    const image = (line.featured_image && line.featured_image.url) || line.image || null;
    const url = new URL(built ? `${root}cart` : (line.url || `${root}products/${handleOf(line)}`), window.location.origin).href;
    const title = built ? `Your ${name}` : browseTitle(line.variant_title);
    const variantId = String(line.variant_id || line.id || '');
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || config.currency;
    const properties = {
      Name: name, ProductName: name, ProductID: String(line.product_id || ''), Brand: 'CITYFRAMES',
      Price: price, Value: price, $value: price, ImageURL: image, URL: url, categories: line.product_type ? [line.product_type] : [],
      variantIds: [variantId], 'Variant Id': [variantId], variantTitles: [title], 'Variant Title': [title],
      productTitles: [name], currency_code: [currency], source: 'cart',
      items: [{
        productId: String(line.product_id || ''), variantId, productTitle: name, variantTitle: title, price, currency,
        quantity, sku: line.sku || '', url, imageURL: image, ImageURL: image, brand: 'CITYFRAMES', category: line.product_type || ''
      }]
    };
    if (built) Object.assign(properties, { built, cityIds: lead.cities });
    if (consent === null) browseQueue.push(properties);
    else pushViewedCity(properties);
  }

  /**
   * `/cart/add` asked for JSON (not `.js`) answers in Shopify's older format: prices as decimal
   * strings in the shop's currency ("679.00"), no handle, no final prices. The theme never calls
   * it that way; a bot did, and its £679 CUBE Collection went to Meta as £5.66 (8 Oct 2026). Such
   * lines are read back from /cart.js, which has the usual cents and handles, by their line key.
   */
  function onCartAdd(response, sent) {
    const lines = Array.isArray(response && response.items) ? response.items : (response && response.id ? [response] : []);
    if (lines.length && lines.some((line) => line.final_line_price === undefined)) {
      const keys = new Set(lines.map((line) => line.key));
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      nativeFetch.call(window, `${root}cart.js`).then((r) => r.json()).then((cart) => {
        const full = ((cart && cart.items) || []).filter((item) => keys.has(item.key));
        if (full.length) {
          const groups = groupsFrom(full, sent);
          report(groups);
          browseFromCart(groups);
        }
      }).catch(() => {});
    } else if (lines.length) {
      const groups = groupsFrom(lines, sent);
      report(groups);
      browseFromCart(groups);
    }
    syncCartIds();  // the first add creates the cart
  }

  /**
   * A quantity raised in the cart is an add to cart of the extra units only; lowering it sends
   * nothing. Shopify lists the difference in `items_added`. A set's merged line is skipped: its
   * cities are not in the change.
   */
  function onCartChange(cart) {
    const added = (cart && Array.isArray(cart.items_added)) ? cart.items_added : [];
    const groups = [];
    added.forEach((item) => {
      const id = String(item.variant_id || '');
      const quantity = Number(item.quantity) || 0;
      if (!id || quantity <= 0 || SET_PARENTS.has(id)) return;
      const line = (cart.items || []).find((l) => String(l.variant_id) === id);
      const unit = line ? (Number(line.final_price) || 0) / 100 : Number(item.presentment_price) || 0;
      const shape = { handle: handleOf(item), product_type: item.product_type };
      groups.push({
        type: lineType(shape), id, quantity, gross: unit * quantity, title: item.product_title,
        category: item.product_type, freight: isFreight(shape), properties: (line && line.properties) || {}
      });
    });
    report(groups);
  }

  /** The configurators price CustomizeProduct as shown; Meta gets it net like everything else. */
  function netCustomize(data) {
    if (!data) return data;
    const factor = netFactor(!!(config.product && config.product.freight));
    const out = { ...data };
    if (Array.isArray(data.contents)) {
      out.contents = data.contents.map((c) => ({ ...c, item_price: round2((Number(c.item_price) || 0) * factor) }));
      out.value = round2(out.contents.reduce((sum, c) => sum + c.item_price * (Number(c.quantity) || 1), 0));
    } else if (data.value !== undefined) {
      out.value = round2((Number(data.value) || 0) * factor);
    }
    return out;
  }

  /** ViewContent for a city the visitor chose, at its net price; Klaviyo gets the shelf price. */
  function viewContent(city) {
    if (!city || !city.variantId || viewed.has(city.variantId)) return;
    viewed.add(city.variantId);
    const p = config.product;
    const price = round2(city.price * netFactor(p.freight));
    track('ViewContent', {
      currency: config.currency,
      value: price,
      content_ids: [city.variantId],
      contents: [{ id: city.variantId, quantity: 1, item_price: price }],
      content_type: 'product',
      content_name: p.title,
      content_category: p.type
    });
    viewedCity(city);
  }

  // --- the cart calls: every add, and every quantity raised -----------------------------------

  window.fetch = function (input, init) {
    const result = nativeFetch.apply(this, arguments);
    try {
      const url = typeof input === 'string' ? input : (input && input.url) || String(input);
      const path = new URL(url, window.location.origin).pathname;
      let handler = null;
      if (CART_ADD.test(path)) {
        const sent = sentItems(init);
        handler = (json) => onCartAdd(json, sent);
      } else if (CART_CHANGE.test(path)) {
        handler = onCartChange;
      }
      if (handler) {
        result.then((response) => {
          if (response && response.ok) response.clone().json().then(handler).catch(() => {});
        }).catch(() => {});
      }
    } catch (error) { /* never get in the way of the cart */ }
    return result;
  };

  // --- newsletter sign-ups ------------------------------------------------------------------
  //
  // Our own form (snippets/cf-newsletter-form, 13 Oct 2026) says `cf:newsletter-signup`; the
  // Klaviyo forms still in use say `klaviyoForms`. Either way a Lead, and with marketing consent the
  // email kept as a hash. Our form also tells Klaviyo who the visitor is, as Klaviyo's forms do.

  function newsletterLead(email) {
    const lead = (hash) => {
      if (hash && consent && consent.marketing) store.set('cf_em', hash);
      track('Lead', { content_name: 'newsletter' }, hash ? { em: hash } : undefined);
    };
    if (typeof email === 'string' && email.includes('@')) {
      sha256(email.trim().toLowerCase()).then(lead, () => lead(null));
    } else {
      lead(null);
    }
  }

  window.addEventListener('klaviyoForms', (event) => {
    const detail = event.detail || {};
    if (detail.type !== 'submit') return;
    const meta = detail.metaData || {};
    newsletterLead(meta.$email || meta.email);
  });

  window.addEventListener('cf:newsletter-signup', (event) => {
    const email = event.detail && event.detail.email;
    newsletterLead(email);
    rememberEmail(email);
  });

  // --- page events ---------------------------------------------------------------------------

  track('PageView');

  // A city the visitor chose: the product URL carries ?variant= (every configurator switches city
  // by navigating there, and ads link there), or the clearance picker swapped it in place. The
  // default city on a bare product URL is not a view — it would pile views on the default city.
  const p = config.product;
  if (p && p.selected) viewContent(p);

  // --- a custom-location design made (9 Oct 2026) --------------------------------------------
  //
  // CreateCustomCube / CreateCustomFrame (custom events): the shopper's own design came back from
  // the renderer — `cf:cube:preview-ready`, which both configurators send (the FRAME's is a copy
  // of the CUBE's; `data-cf-product` on its root says which). A saved design reopened from its
  // emailed link is not one: the configurator marks that run's `cf:cube:preview-start` as
  // `restore`. Once per product a day for a visitor — a shopper tries several places, and the
  // signal is the person who made a design, not their fifth try. Valued at the product's net
  // price; the place itself is never sent, as it is often someone's home.
  const CREATED = { cube: 'CreateCustomCube', frame: 'CreateCustomFrame' };
  const CREATED_KEY = 'cf_created';
  const designer = document.querySelector('[data-cf-product]');
  const created = designer && CREATED[designer.getAttribute('data-cf-product')];
  if (created && p) {
    let restoring = false;
    let sentHere = false;
    document.addEventListener('cf:cube:preview-start', (event) => {
      restoring = !!(event.detail && event.detail.restore);
    });
    document.addEventListener('cf:cube:preview-ready', () => {
      if (restoring) {
        restoring = false;
        return;
      }
      if (sentHere || (consent && !consent.marketing)) return;
      const day = new Date().toISOString().slice(0, 10);
      let days = {};
      try { days = JSON.parse(store.get(CREATED_KEY) || '{}') || {}; } catch (error) { days = {}; }
      if (days[created] === day) return;
      sentHere = true;
      if (consent && consent.marketing) store.set(CREATED_KEY, JSON.stringify({ ...days, [created]: day }));
      const price = round2((Number(p.price) || 0) * netFactor(p.freight));
      track(created, {
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || config.currency,
        value: price,
        content_ids: [String(p.variantId)],
        contents: [{ id: String(p.variantId), quantity: 1, item_price: price }],
        content_type: 'product',
        content_name: p.title,
        content_category: 'Custom Location'
      });
    });
  }

  // --- the CUBE Set (6 Oct 2026) -------------------------------------------------------------
  //
  // Few events, each meaning one thing: one ViewContent per page view, with the size shown;
  // CustomizeProduct once the Set is complete, with its cities as catalog items and the Set's net
  // value, again only if the Set then changes; QualifiedVisit at the second city. The page tells
  // us its state through `cf:set-change` (sections/set-product.liquid), sent on a shopper's change
  // only. The size variants are not in the Meta catalog (no fb_sync, Alex 6 Oct): the ViewContent
  // still says "looked at the Set", it just cannot name a catalog product.
  if (p && p.handle === 'cube-set-individual' && !p.selected) {
    const setView = () => {
      const set = window.CF && window.CF.SET;
      const shown = set && set.variants && set.variants[set.count];
      viewContent(shown
        ? { ...p, variantId: String(shown.id), variantTitle: `${set.count} Cities`, price: (Number(shown.price) || 0) / 100 }
        : p);
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', setView);
    else setView();
  }

  let lastSet = '';
  document.addEventListener('cf:set-change', (event) => {
    const detail = event.detail || {};
    const slots = (detail.cities || []).map((id) => (id ? String(id) : ''));
    const chosen = slots.filter(Boolean);
    if (chosen.length >= 2) qualify('set');
    const count = Number(detail.count) || slots.length;
    if (!count || chosen.length !== count) return;
    const signature = `${count}:${chosen.slice().sort(byId).join(',')}`;
    if (signature === lastSet) return;
    lastSet = signature;
    // What the page shows for this Set: its cities' own prices less the Set's discount — a clearance
    // city makes the Set cheaper than its size's list price (setDiscountCents, the page's own). Made
    // net as a whole and then shared, as an add to cart is, so both send the same cents.
    const set = window.CF && window.CF.SET;
    const cities = (window.CF && window.CF.SET_CITIES) || [];
    const singles = cities.reduce((sum, c) => sum + ((c && Number(c.price)) || 0), 0);
    const off = typeof window.setDiscountCents === 'function' ? window.setDiscountCents(count) : 0;
    const size = set && set.variants && set.variants[count];
    const gross = singles > 0 ? Math.max(singles - off, 0) / 100 : (size ? (Number(size.price) || 0) / 100 : 0);
    const net = round2(gross * netFactor(false));
    const share = round2(net / count);
    const units = chosen.slice().sort(byId);
    track('CustomizeProduct', {
      currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || config.currency,
      value: net,
      content_ids: Array.from(new Set(units)),
      contents: units.map((id, i) => ({ id, quantity: 1, item_price: i === count - 1 ? round2(net - share * (count - 1)) : share })),
      content_name: 'CUBE Set',
      content_category: 'CUBE Set',
      set_type: 'cube_set',
      set_size: count
    });
  });

  document.addEventListener('variant:change', (event) => {
    const variant = event.detail && event.detail.variant;
    if (!p || !variant || !variant.id) return;
    const media = variant.featured_media && variant.featured_media.preview_image;
    const src = (media && media.src) || (variant.featured_image && variant.featured_image.src);
    const url = new URL(window.location.href);
    viewContent({
      variantId: String(variant.id),
      variantTitle: variant.title,
      sku: variant.sku,
      price: (Number(variant.price) || 0) / 100,
      compareAtPrice: (Number(variant.compare_at_price) || 0) / 100,
      url: `${url.origin}${url.pathname}?variant=${variant.id}`,
      image: src ? (src.startsWith('//') ? `https:${src}` : src) : p.image
    });
  });

  if (config.collection) {
    track('ViewCategory', {
      content_name: config.collection.title,
      content_category: config.collection.handle,
      content_ids: config.collection.variantIds,
      content_type: 'product'
    });
  }

  if (config.search) track('Search', { search_string: config.search });

  // Configurator calls from before this script loaded, then the live entry point. A pick goes out
  // once per kind (its category: Display Cover, Engraved Plaque, a GRAND option) per page view —
  // toggling a cover on and off sent one visitor's fourteen (6 Oct 2026); the first pick is the signal.
  const early = (window.cfTrack && window.cfTrack.q) || [];
  const picked = new Set();
  window.cfTrack = (name, data) => {
    if (!FROM_THEME.has(name)) return;
    const kind = (data && (data.content_category || data.content_name)) || '';
    if (kind && picked.has(kind)) return;
    if (kind) picked.add(kind);
    track(name, netCustomize(data));
  };
  early.forEach((args) => window.cfTrack(args[0], args[1]));

  // window.cfGa4 (above): calls from before this script loaded, then the live entry point.
  const earlyGa4 = (window.cfGa4 && window.cfGa4.q) || [];
  window.cfGa4 = (name, params) => {
    if (name) themeGa4.push([String(name), params || {}]);
    releaseThemeGa4();
  };
  earlyGa4.forEach((args) => window.cfGa4(args[0], args[1]));

  // --- how far a page is read, and what is clicked on it (16 Oct 2026) -----------------------
  //
  // On the pages in config.engagementPages (path without language folder; today only the CUBE
  // overview), a section of the page counts as reached once its top passes the middle of the
  // screen (or the page is scrolled to its end with the section on screen), and every click on a
  // link or button is noted with the section it sits in. Sections are the theme's template
  // sections in page order (section_index 1, 2, …); header, announcement bar and footer clicks
  // keep their own name with index 0.
  //
  // The event server gets the page's record as PageEngagement whenever the page is hidden
  // (leaving it, switching tabs) — each time the whole record so far with a running seq, and the
  // report (cityframes-tracking: tracking/engagement.py) keeps each page view's last one. It is
  // stored only, never forwarded. GA4 gets section_view (once per section) and cta_click, with
  // section_name / section_index as custom dimensions and its own link_text / link_url.
  // A page with a configurator can add what is chosen at that moment: window.cfConfigState() is
  // asked at each report and its answer rides along as `config` (the GRAND configurator: frame and
  // background kept, the ones tried, add to cart; 17 Oct 2026). When the visitor picked any of its
  // options, GA4 also gets `<kind>_choice` with the kept options as `<kind>_<option>` — for the
  // GRAND: grand_choice with grand_frame / grand_background, the parameters its add_to_cart carries.
  // Same consent as every other event: nothing without marketing consent.

  const engagementPages = config.engagementPages || [];
  if (engagementPages.includes(contentGroup())) {
    const pageId = uuid();
    const startedAt = Date.now();
    const sectionKey = (el) => el.id.replace(/^shopify-section-/, '').replace(/^.*__/, '');
    // "cube_how_many_HM7plc" → "cube_how_many": the editor's random suffix off, readable in GA4.
    const sectionName = (key) => key.replace(/_[A-Za-z0-9]{5,8}$/, '');
    const clean = (text, max) => String(text || '').replace(/\s+/g, ' ').trim().slice(0, max);
    let sections = [];
    let deepest = 0;     // furthest scroll position reached, as the line half a screen below its top
    let scrolled = 0;    // furthest scroll, in % of the page's height
    let seq = 0;
    const clicks = [];
    const gaWaiting = [];

    // GA4 events from before the banner was answered wait here, like the server's events do.
    const releaseGa4 = () => {
      if (consent === null || !gaWaiting.length) return;
      const due = gaWaiting.splice(0);
      if (!consent.marketing) return;
      loadGoogle();
      due.forEach(([n, p]) => gtag('event', n, p));
    };
    const toGa4 = (name, params) => {
      if (!googleOn) return;
      gaWaiting.push([name, { ...params, content_group: contentGroup(), ...(googleDebug ? { debug_mode: true } : {}) }]);
      releaseGa4();
    };

    const collectSections = () => {
      let index = 0;
      sections = Array.from(document.querySelectorAll('.shopify-section[id^="shopify-section-template--"]'))
        .filter((el) => el.offsetHeight > 0)
        .map((el) => {
          const heading = el.querySelector('h1, h2, h3, .h1, .h2, .h3');
          return { el, index: ++index, key: sectionKey(el), label: clean(heading && (heading.innerText || heading.textContent), 80), reached: false };
        });
    };

    const update = () => {
      releaseGa4();
      const view = window.innerHeight;
      const page = document.documentElement.scrollHeight || 1;
      const atEnd = window.scrollY + view >= page - 2;
      deepest = Math.max(deepest, window.scrollY + view / 2);
      scrolled = Math.max(scrolled, Math.min(100, Math.round(((window.scrollY + view) / page) * 100)));
      sections.forEach((section) => {
        if (section.reached) return;
        const top = section.el.getBoundingClientRect().top + window.scrollY;
        if (top < deepest || (atEnd && top < window.scrollY + view)) {
          section.reached = true;
          toGa4('section_view', { section_name: sectionName(section.key), section_index: section.index });
        }
      });
    };

    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => { frame = 0; update(); });
    };

    const start = () => {
      collectSections();
      update();
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
    else start();

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element && event.target.closest('a[href], button, summary, [role="button"]');
      if (!target || clicks.length >= 40 || target.closest('[id^="shopify-pc__"]')) return;  // not the cookie banner
      // Drawers and modals move themselves to <body> when they open (theme.js), out of their
      // section: those go by the element under <body> they sit in ("sidebar-menu", "cart-drawer").
      const wrapper = target.closest('.shopify-section');
      let holder = target;
      while (!wrapper && holder.parentElement && holder.parentElement !== document.body) holder = holder.parentElement;
      const key = wrapper ? sectionKey(wrapper) : (holder.id || holder.localName || '').replace(/[^A-Za-z0-9_-]/g, '-').slice(0, 80);
      const section = sections.find((s) => s.el === wrapper);
      const image = target.querySelector('img');
      const text = clean(target.getAttribute('aria-label') || target.innerText || (image && image.alt), 80);
      let href = '';
      if (target.matches('a[href]')) {
        try {
          const url = new URL(target.href, window.location.href);
          href = url.origin === window.location.origin ? `${url.pathname}${url.search}${url.hash}` : `${url.origin}${url.pathname}`;
        } catch (error) { /* not a URL */ }
      }
      const kind = href ? 'link' : 'button';
      clicks.push([Math.round((Date.now() - startedAt) / 1000), section ? section.index : 0, key, kind, text, href.slice(0, 300)]);
      toGa4('cta_click', {
        section_name: sectionName(key) || 'page', section_index: section ? section.index : 0, link_text: text, ...(href ? { link_url: href } : {})
      });
    }, true);

    // Leaving a page fires both "hidden" and pagehide: a record that has not changed since the
    // last one is not sent again.
    let lastReported = '';
    let lastChoice = '';
    const reportChoice = (chosen) => {
      if (!chosen || !chosen.kind) return;
      const options = Object.keys(chosen).filter((key) => Array.isArray(chosen[`tried_${key}`]));
      const choice = options.map((key) => chosen[key]).join('|');
      if (!options.some((key) => chosen[`tried_${key}`].length) || choice === lastChoice) return;
      lastChoice = choice;
      const params = {};
      options.forEach((key) => { params[`${chosen.kind}_${key}`] = String(chosen[key]); });
      toGa4(`${chosen.kind}_choice`, params);
    };
    const report = () => {
      if (!consent || !consent.marketing || !sections.length) return;
      update();
      let config = null;
      try { config = typeof window.cfConfigState === 'function' ? window.cfConfigState() : null; } catch (error) { config = null; }
      reportChoice(config);
      const state = `${scrolled}|${sections.filter((s) => s.reached).length}|${clicks.length}|${config ? JSON.stringify(config) : ''}`;
      if (state === lastReported) return;
      lastReported = state;
      outbox.push({
        event_id: uuid(),
        name: 'PageEngagement',
        source: 'storefront',
        ts: Date.now(),
        url: window.location.href,
        referrer: document.referrer || undefined,
        data: {
          engagement: {
            page_id: pageId,
            seq: ++seq,
            secs: Math.round((Date.now() - startedAt) / 1000),
            vw: window.innerWidth,
            vh: window.innerHeight,
            scroll: scrolled,
            sections: sections.map((s) => [s.index, s.key, s.label, s.reached ? 1 : 0]),
            clicks: clicks.slice(),
            ...(config ? { config } : {})
          }
        }
      });
      flush();
    };
    window.addEventListener('pagehide', report);
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') report(); });
  }
})();
